import { NotificationEvent, NotificationMessage, NotificationType, formatNotification } from './NotificationTypes';
import { FactionType } from './types';
import { SoundManager } from './SoundManager';
import { sanitizeInput } from './security';

export class NotificationManager {
  private queue: NotificationMessage[] = [];
  private displayedNotifications: Map<number, HTMLElement> = new Map();
  private nextId = 0;
  private viewerFaction: FactionType;
  private soundManager: SoundManager;
  private container: HTMLElement | null = null;
  
  constructor(viewerFaction: FactionType, soundManager: SoundManager) {
    this.viewerFaction = viewerFaction;
    this.soundManager = soundManager;
    this.createContainer();
  }
  
  private createContainer(): void {
    // Remove existing container if any
    const existing = document.getElementById('notification-queue');
    if (existing) existing.remove();
    
    // Create new container
    this.container = document.createElement('div');
    this.container.id = 'notification-queue';
    this.container.style.cssText = `
      position: fixed;
      top: 80px;
      left: 50%;
      transform: translateX(-50%);
      width: 400px;
      max-height: 400px;
      pointer-events: none;
      z-index: 1000;
    `;
    document.body.appendChild(this.container);
  }
  
  public setViewerFaction(faction: FactionType): void {
    this.viewerFaction = faction;
  }
  
  public processEvent(event: NotificationEvent, isViewer: boolean = true): void {
    const message = formatNotification(event, this.viewerFaction, isViewer);
    if (message) {
      this.addNotification(message);
    }
  }
  
  public addNotification(message: NotificationMessage): void {
    // Add to queue
    this.queue.push(message);
    
    // Display immediately
    this.displayNotification(message);
    
    // Play sound if specified
    if (message.sound) {
      switch (message.sound) {
        case 'success':
          this.soundManager.playBuildUnit();
          break;
        case 'error':
          this.soundManager.playError();
          break;
        case 'warning':
          this.soundManager.playMenuSelect();
          break;
        case 'info':
          this.soundManager.playClick();
          break;
      }
    }
  }
  
  private displayNotification(message: NotificationMessage): void {
    if (!this.container) return;
    
    // Check if notifications are enabled
    const notificationsEnabled = localStorage.getItem('notificationsEnabled') !== 'false';
    if (!notificationsEnabled) return;
    
    // Get notification duration from settings
    const savedDuration = localStorage.getItem('notificationDuration') || '10';
    const actualDuration = savedDuration === 'manual' ? -1 : parseInt(savedDuration) * 1000;
    
    const id = this.nextId++;
    
    // Create container for notification and progress bar
    const notificationContainer = document.createElement('div');
    notificationContainer.style.cssText = `
      margin-bottom: 10px;
      opacity: 0;
      transform: translateX(100%);
      transition: all 0.3s ease-out;
    `;
    
    const notification = document.createElement('div');
    notification.className = `notification notification-${message.priority}`;
    notification.style.cssText = `
      background: linear-gradient(135deg, 
        ${this.getTypeColor(message.type)}ee, 
        ${this.getTypeColor(message.type)}cc);
      border: 2px solid ${this.getTypeBorderColor(message.type)};
      border-radius: 8px;
      padding: 12px 16px;
      padding-bottom: 14px;
      color: #fff;
      font-family: 'Courier New', monospace;
      font-size: 14px;
      font-weight: bold;
      text-shadow: 2px 2px 4px rgba(0,0,0,0.5);
      box-shadow: 0 4px 8px rgba(0,0,0,0.3);
      pointer-events: auto;
      cursor: pointer;
      position: relative;
      overflow: hidden;
    `;
    
    // Create DOM elements safely to prevent XSS
    const contentDiv = document.createElement('div');
    contentDiv.style.cssText = 'display: flex; align-items: center; justify-content: space-between; gap: 10px;';
    
    const leftContent = document.createElement('div');
    leftContent.style.cssText = 'display: flex; align-items: center; gap: 10px; flex: 1;';
    
    // Add icon safely
    const iconSpan = document.createElement('span');
    iconSpan.innerHTML = this.getIcon(message.type); // Icons are safe (controlled content)
    leftContent.appendChild(iconSpan);
    
    // Add text safely (prevents XSS)
    const textSpan = document.createElement('span');
    textSpan.textContent = sanitizeInput(message.text);
    leftContent.appendChild(textSpan);
    
    contentDiv.appendChild(leftContent);
    
    // Add close button
    const closeButton = document.createElement('button');
    closeButton.textContent = '✕';
    closeButton.style.cssText = `
      background: transparent;
      border: none;
      color: rgba(255, 255, 255, 0.7);
      font-size: 18px;
      cursor: pointer;
      padding: 0;
      width: 24px;
      height: 24px;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: color 0.2s ease;
    `;
    
    closeButton.onmouseover = () => {
      closeButton.style.color = 'rgba(255, 255, 255, 1)';
    };
    
    closeButton.onmouseout = () => {
      closeButton.style.color = 'rgba(255, 255, 255, 0.7)';
    };
    
    // Click to dismiss
    const dismissNotification = () => {
      this.removeNotification(id, notificationContainer);
    };
    
    closeButton.onclick = (e) => {
      e.stopPropagation();
      dismissNotification();
    };
    
    notification.onclick = dismissNotification;
    
    contentDiv.appendChild(closeButton);
    notification.appendChild(contentDiv);
    notificationContainer.appendChild(notification);
    
    // Add progress bar if not in manual mode
    if (actualDuration > 0) {
      const progressContainer = document.createElement('div');
      progressContainer.style.cssText = `
        position: absolute;
        bottom: 0;
        left: 0;
        right: 0;
        height: 2px;
        background: rgba(0, 0, 0, 0.2);
        overflow: hidden;
      `;
      
      const progressBar = document.createElement('div');
      progressBar.style.cssText = `
        height: 100%;
        width: 100%;
        background: ${this.getTypeColor(message.type)}aa;
        box-shadow: 0 0 3px ${this.getTypeColor(message.type)};
        transition: transform ${actualDuration}ms linear;
        transform-origin: left center;
      `;
      
      progressContainer.appendChild(progressBar);
      notification.appendChild(progressContainer);
      
      // Start the progress animation after the notification appears
      setTimeout(() => {
        progressBar.style.transform = 'scaleX(0)';
      }, 350);
      
      // Auto-remove after duration
      setTimeout(() => {
        this.removeNotification(id, notificationContainer);
      }, actualDuration);
    }
    
    this.container.appendChild(notificationContainer);
    this.displayedNotifications.set(id, notificationContainer);
    
    // Animate in
    requestAnimationFrame(() => {
      notificationContainer.style.opacity = '1';
      notificationContainer.style.transform = 'translateX(0)';
    });
  }
  
  private removeNotification(id: number, element: HTMLElement): void {
    if (!this.displayedNotifications.has(id)) return;
    
    // Animate out
    element.style.opacity = '0';
    element.style.transform = 'translateX(100%)';
    
    setTimeout(() => {
      element.remove();
      this.displayedNotifications.delete(id);
    }, 300);
  }
  
  private getTypeColor(type: NotificationType): string {
    switch (type) {
      // Combat - Red shades
      case NotificationType.UNIT_DESTROYED:
      case NotificationType.MINE_DESTROYED_UNIT:
        return '#cc2222';
      case NotificationType.UNIT_DAMAGED:
      case NotificationType.MINE_DETONATED:
        return '#ff6633';
        
      // Healing/Repair - Green shades  
      case NotificationType.UNIT_HEALED:
        return '#22aa22';
        
      // City events - Gold/Yellow shades
      case NotificationType.CITY_CAPTURED:
      case NotificationType.CITY_CAPTURE_STARTED:
        return '#ffaa00';
      case NotificationType.CITY_LOST:
      case NotificationType.CITY_CAPTURE_INTERRUPTED:
        return '#ff6600';
        
      // Training - Blue shades
      case NotificationType.UNIT_TRAINING_COMPLETE:
      case NotificationType.UNIT_TRAINING_STARTED:
        return '#3366ff';
        
      // Transport - Cyan shades
      case NotificationType.UNIT_BOARDED:
      case NotificationType.UNIT_DISEMBARKED:
      case NotificationType.PARATROOPERS_DEPLOYED:
        return '#00aacc';
        
      // Stance - Purple shades
      case NotificationType.UNIT_HUNKERED:
      case NotificationType.UNIT_STOOD_UP:
        return '#9933ff';
        
      // Turn events - Gray
      case NotificationType.TURN_START:
      case NotificationType.TURN_END:
        return '#888888';
        
      // UI events - Orange
      case NotificationType.UNIT_CYCLE:
        return '#ff9933';
        
      // Connection - Orange
      case NotificationType.PLAYER_CONNECTED:
      case NotificationType.PLAYER_DISCONNECTED:
        return '#ff8800';
        
      // Victory - Special colors
      case NotificationType.GAME_WON:
        return '#00ff00';
      case NotificationType.GAME_LOST:
        return '#ff0000';
        
      // Mines - Orange/Red
      case NotificationType.MINE_PLACED:
        return '#ff9900';
        
      default:
        return '#3366ff';
    }
  }
  
  private getTypeBorderColor(type: NotificationType): string {
    // Slightly brighter version of the base color
    const baseColor = this.getTypeColor(type);
    // Simple brightening by replacing first digit with higher value
    return baseColor.replace(/#(..)(..)(..)/, (_, r, g, b) => {
      const brighten = (hex: string) => {
        const val = parseInt(hex, 16);
        const brightened = Math.min(255, val + 40);
        return brightened.toString(16).padStart(2, '0');
      };
      return '#' + brighten(r) + brighten(g) + brighten(b);
    });
  }
  
  private getIcon(type: NotificationType): string {
    const iconStyle = 'width: 20px; height: 20px; fill: currentColor;';
    
    switch (type) {
      case NotificationType.UNIT_DESTROYED:
      case NotificationType.MINE_DESTROYED_UNIT:
        return `<svg style="${iconStyle}" viewBox="0 0 24 24"><path d="M13 13h-2V7h2m0 10h-2v-2h2M12 2A10 10 0 0 0 2 12a10 10 0 0 0 10 10 10 10 0 0 0 10-10A10 10 0 0 0 12 2z"/></svg>`;
        
      case NotificationType.CITY_CAPTURED:
      case NotificationType.CITY_LOST:
        return `<svg style="${iconStyle}" viewBox="0 0 24 24"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>`;
        
      case NotificationType.UNIT_TRAINING_COMPLETE:
        return `<svg style="${iconStyle}" viewBox="0 0 24 24"><path d="M9 11H7v2h2v-2zm4 0h-2v2h2v-2zm4 0h-2v2h2v-2zm2-7h-1V2h-2v2H8V2H6v2H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 16H5V9h14v11z"/></svg>`;
        
      case NotificationType.MINE_DETONATED:
        return `<svg style="${iconStyle}" viewBox="0 0 24 24"><path d="M11.5 6.5l.5 1.5h2l-1.5 1 .5 1.5-1.5-1-1.5 1 .5-1.5-1.5-1h2l.5-1.5M11 16l2.4-3.4L19 10l-7 .1L9 3l-1 7.1L1 11l5.6 2.6L5 21l6-5z"/></svg>`;
        
      case NotificationType.TURN_START:
      case NotificationType.TURN_END:
        return `<svg style="${iconStyle}" viewBox="0 0 24 24"><path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10 10-4.5 10-10S17.5 2 12 2zm4.2 14.2L11 13V7h1.5v5.2l4.5 2.7-.8 1.3z"/></svg>`;
        
      default:
        return `<svg style="${iconStyle}" viewBox="0 0 24 24"><circle cx="12" cy="12" r="8"/></svg>`;
    }
  }
  
  public clear(): void {
    this.queue = [];
    this.displayedNotifications.forEach((element, id) => {
      this.removeNotification(id, element);
    });
  }
  
  public destroy(): void {
    this.clear();
    if (this.container) {
      this.container.remove();
      this.container = null;
    }
  }
}